import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSpecialistCaseMailCcService } from '../../services/specialistCaseMailCcService';

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const SpecialistCaseMailCcSettingsTab: React.FC = () => {
  const { jwtToken, user } = useAuth();
  const service = useSpecialistCaseMailCcService();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ccEmailsText, setCcEmailsText] = useState('');
  const [ccCurrentUser, setCcCurrentUser] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updatedMeta, setUpdatedMeta] = useState<string | null>(null);

  const parseEmailsFromText = (text: string): string[] => {
    const parts = text.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const p of parts) {
      const key = p.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(p);
      }
    }
    return unique;
  };

  const load = useCallback(async () => {
    if (!jwtToken) {
      setError('No hay token de autenticación');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await service.getConfig();
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        setError('No tiene permiso para ver esta configuración (se requiere rol ADMIN).');
      } else {
        setError(res.error || 'No se pudo cargar la configuración');
      }
    } else if (res.data) {
      setCcEmailsText((res.data.ccEmails || []).join('\n'));
      setCcCurrentUser(Boolean(res.data.ccCurrentUser));
      if (res.data.updatedAt) {
        setUpdatedMeta(
          `Última actualización: ${new Date(res.data.updatedAt).toLocaleString('es-MX')}` +
            (res.data.updatedByUsername ? ` por ${res.data.updatedByUsername}` : '')
        );
      }
    }
    setLoading(false);
  }, [jwtToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    const emails = parseEmailsFromText(ccEmailsText);
    const invalid = emails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length > 0) {
      setError(`Correo(s) inválido(s): ${invalid.join(', ')}`);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const res = await service.updateConfig({
      ccEmails: emails,
      ccCurrentUser
    });
    if (!res.ok) {
      setError(res.error || 'No se pudo guardar');
    } else {
      setSuccess('Configuración de copias (CC) guardada.');
      if (res.data) {
        setCcEmailsText((res.data.ccEmails || []).join('\n'));
        setCcCurrentUser(Boolean(res.data.ccCurrentUser));
      }
      await load();
    }
    setSaving(false);
  };

  const previewEmails = parseEmailsFromText(ccEmailsText);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            Copias de correo — canalización
          </Typography>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={() => void load()}
            disabled={loading || saving}
          >
            Recargar
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Estos correos reciben copia (CC) de las notificaciones enviadas al especialista al terminar la
          llamada o al canalizar un caso ya finalizado. Por defecto también se incluye al usuario que
          realiza la acción.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="Correos en copia (CC)"
              value={ccEmailsText}
              onChange={(e) => setCcEmailsText(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              placeholder={'supervisor@empresa.com\ncoordinacion@empresa.com'}
              helperText="Uno por línea, o separados por coma / punto y coma."
            />

            {previewEmails.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {previewEmails.map((e) => (
                  <Chip key={e} label={e} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={ccCurrentUser}
                  onChange={(e) => setCcCurrentUser(e.target.checked)}
                />
              }
              label={
                user?.email
                  ? `Incluir siempre al usuario logueado (${user.email})`
                  : 'Incluir siempre al usuario logueado (si tiene correo en su cuenta)'
              }
            />

            {updatedMeta && (
              <Typography variant="caption" color="text.secondary">
                {updatedMeta}
              </Typography>
            )}

            <Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={() => void handleSave()}
                disabled={saving}
              >
                Guardar
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SpecialistCaseMailCcSettingsTab;
